import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import DarkLayout from './DarkLayout';
import { HeaderPrimaryAddButton } from './HeaderPrimaryAddButton';
import ProjectFormDialog from './ProjectFormDialog';
import SearchBar from './SearchBar';
import {
  ListPageRoot,
  ListPageMetrics,
  ListPageMetricCard,
  ListPageToolbar,
  ListPageFilterRow,
  listPageFilterPillActive,
  listPageFilterPillInactive,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageInlineError,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
  ListPageTablePlaceholder,
  ListPageMobilePlaceholder,
} from './listPage';
import { apiService } from '../services/api';
import { Project as ProjectType } from '../types/api';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';

type StatusFilter = 'All' | 'Active' | 'Draft' | 'OnHold' | 'Completed';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'In Progress' },
  { value: 'Draft', label: 'Proposal' },
  { value: 'OnHold', label: 'On Hold' },
];

function formatBudget(v?: number): string {
  if (v == null) return '—';
  return formatCurrencyCompact(v);
}

function formatDate(s?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'Active': return 'In Progress';
    case 'Draft': return 'Proposal';
    case 'OnHold': return 'On Hold';
    case 'Completed': return 'Completed';
    default: return status;
  }
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400';
    case 'Draft':
      return 'bg-amber-500/10 border border-amber-500/30 text-amber-400';
    case 'OnHold':
      return 'bg-slate-700/50 border border-slate-600/50 text-slate-300';
    case 'Completed':
      return 'bg-blue-500/10 border border-blue-500/30 text-blue-400';
    default:
      return 'bg-slate-700/50 border border-slate-600/50 text-slate-300';
  }
}

const Projects: React.FC = () => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreateProject = hasPermission(user, permissions.projects.create);
  const canEditProject = hasPermission(user, permissions.projects.edit);
  const canDeleteProject = hasPermission(user, permissions.projects.delete);
  const canViewEstimates = hasPermission(user, permissions.estimates.view);
  useDocumentTitle('Projects');
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await apiService.getProjects();
      setProjects(list);
    } catch (err) {
      console.error(err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (location.pathname === '/projects/new') {
      setDialogOpen(true);
    }
  }, [location.pathname]);

  const filteredProjects = useMemo(() => {
    let list = projects;
    if (statusFilter !== 'All') {
      list = list.filter((p) => p.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.description?.toLowerCase().includes(q)) ||
          (p.siteAddress?.toLowerCase().includes(q)) ||
          (p.clientName?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [projects, statusFilter, searchQuery]);

  const summaryTotalBudget = useMemo(() => {
    return projects
      .filter((p) => p.status === 'Active' && p.budget != null)
      .reduce((sum, p) => sum + (p.budget ?? 0), 0);
  }, [projects]);

  const activeCount = useMemo(() => projects.filter((p) => p.status === 'Active').length, [projects]);

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (p: ProjectType) => {
    setEditingId(p.projectId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (location.pathname === '/projects/new') {
      navigate('/projects');
    }
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDeleteProject) return;
    const confirmed = await confirmDelete({ message: 'Delete this project and all its estimates?' });
    if (!confirmed) return;
    try {
      await apiService.deleteProject(id);
      loadProjects();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DarkLayout
      title="Projects"
      activeTab="projects"
      headerRight={
        canCreateProject ? (
          <HeaderPrimaryAddButton label="Create Project" onClick={openCreate} mobileAriaLabel="Create project" />
        ) : null
      }
    >
      <ListPageRoot>
        <ListPageMetrics>
          <ListPageMetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <span className="material-symbols-outlined text-sm text-accent-blue">folder_open</span>
              <p className="text-[10px] font-bold uppercase tracking-widest">Total Projects</p>
            </div>
            <p className="text-white text-3xl font-bold leading-tight">{projects.length}</p>
            {activeCount > 0 && (
              <div className="mt-1 flex items-center gap-1 text-emerald-400 text-xs font-semibold">
                <span className="material-symbols-outlined text-xs">trending_up</span>
                <span>+{activeCount} active</span>
              </div>
            )}
          </ListPageMetricCard>
          <ListPageMetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <span className="material-symbols-outlined text-sm text-accent-blue">payments</span>
              <p className="text-[10px] font-bold uppercase tracking-widest">Active Budget</p>
            </div>
            <p className="text-white text-3xl font-bold leading-tight">{formatBudget(summaryTotalBudget)}</p>
            <div className="mt-1 flex items-center gap-1 text-accent-blue text-xs font-semibold">
              <span className="material-symbols-outlined text-xs">info</span>
              <span>{projects.filter((p) => p.budget != null && p.budget > 0).length} projects with budget</span>
            </div>
          </ListPageMetricCard>
          <ListPageMetricCard className="hidden lg:flex">
            <div className="flex items-center gap-2 text-slate-400">
              <span className="material-symbols-outlined text-sm text-accent-blue">schedule</span>
              <p className="text-[10px] font-bold uppercase tracking-widest">Proposals</p>
            </div>
            <p className="text-white text-3xl font-bold leading-tight">
              {projects.filter((p) => p.status === 'Draft').length}
            </p>
            <div className="mt-1 flex items-center gap-1 text-amber-400 text-xs font-semibold">
              <span className="material-symbols-outlined text-xs">description</span>
              <span>Draft status</span>
            </div>
          </ListPageMetricCard>
        </ListPageMetrics>

        <ListPageToolbar>
          <SearchBar
            variant="listPage"
            placeholder="Search projects, clients..."
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <ListPageFilterRow>
            <button
              type="button"
              onClick={() => setStatusFilter('All')}
              className={`flex h-9 shrink-0 items-center justify-center gap-2 rounded-full px-5 text-xs shadow-md ${
                statusFilter === 'All' ? listPageFilterPillActive : `${listPageFilterPillInactive} font-medium`
              }`}
            >
              All
            </button>
            {STATUS_OPTIONS.filter((o) => o.value !== 'All').map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                className={`flex h-9 shrink-0 items-center justify-center gap-2 rounded-full px-5 text-xs ${
                  statusFilter === opt.value
                    ? listPageFilterPillActive
                    : listPageFilterPillInactive
                }`}
              >
                {opt.label}
              </button>
            ))}
          </ListPageFilterRow>
        </ListPageToolbar>

        {error && <ListPageInlineError>{error}</ListPageInlineError>}

        <ListPageContentSection>
          <ListPageTableSurface>
            {loading ? (
              <ListPageTablePlaceholder>Loading projects...</ListPageTablePlaceholder>
            ) : filteredProjects.length === 0 ? (
              <ListPageTablePlaceholder>
                {projects.length === 0
                  ? 'No projects yet. Create one to get started.'
                  : 'No projects match your search or filter.'}
              </ListPageTablePlaceholder>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <ListPageTableHeadRow>
                    <ListPageTh>Project Name</ListPageTh>
                    <ListPageTh>Site Address</ListPageTh>
                    <ListPageTh>Status</ListPageTh>
                    <ListPageTh>Budget</ListPageTh>
                    <ListPageTh>Last Modified</ListPageTh>
                    <ListPageTh className="text-right">Actions</ListPageTh>
                  </ListPageTableHeadRow>
                </thead>
                <tbody className={listPageTableBodyClass}>
                  {filteredProjects.map((p) => (
                    <tr key={p.projectId} className={listPageTableRowClass}>
                      <td className="px-5 py-4">
                        <p
                          className={`text-white font-semibold text-sm ${p.status === 'Completed' ? 'line-through' : ''}`}
                        >
                          {p.name}
                        </p>
                        <p className="text-slate-500 text-[10px] uppercase font-medium mt-0.5">
                          {p.clientName ?? ''}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-300 text-sm">{p.siteAddress ?? `—`}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full ${getStatusBadgeClass(p.status)}`}
                        >
                          {getStatusLabel(p.status)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-white font-bold text-sm">{formatBudget(p.budget)}</p>
                      </td>
                      <td className="px-5 py-4 text-slate-400 text-sm">
                        {formatDate(p.updatedAt ?? p.createdAt)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="action-row-icon-buttons">
                          {canEditProject && (
                            <button
                              type="button"
                              onClick={() => openEdit(p)}
                              className="text-primary hover:bg-primary/10"
                              aria-label="Edit project"
                            >
                              <span className="material-symbols-outlined">edit</span>
                            </button>
                          )}
                          {canViewEstimates && (
                            <button
                              type="button"
                              onClick={() => navigate(`/projects/${p.projectId}/estimates`)}
                              className="text-slate-300 hover:bg-white/5"
                              aria-label="Details"
                            >
                              <span className="material-symbols-outlined">visibility</span>
                            </button>
                          )}
                          {canDeleteProject && (
                            <button
                              type="button"
                              onClick={() => handleDelete(p.projectId)}
                              className="text-red-400 hover:bg-red-500/10"
                              aria-label="Delete"
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ListPageTableSurface>

          <ListPageMobileStack>
            {loading ? (
              <ListPageMobilePlaceholder>Loading projects...</ListPageMobilePlaceholder>
            ) : filteredProjects.length === 0 ? (
              <ListPageMobilePlaceholder>
                {projects.length === 0
                  ? 'No projects yet. Create one to get started.'
                  : 'No projects match your search or filter.'}
              </ListPageMobilePlaceholder>
            ) : (
              filteredProjects.map((p) => (
                <div
                  key={p.projectId}
                  className={`bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-4 ${p.status === 'Completed' ? 'opacity-75' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3
                        className={`text-white font-bold text-lg leading-tight ${p.status === 'Completed' ? 'line-through' : ''}`}
                      >
                        {p.name}
                      </h3>
                      <p className="text-slate-400 text-sm mt-0.5">{p.siteAddress ?? '—'}</p>
                      {p.clientName != null && (
                        <p className="text-slate-500 text-xs mt-0.5">{p.clientName}</p>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full ${getStatusBadgeClass(p.status)}`}
                    >
                      {getStatusLabel(p.status)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700/50">
                    <div>
                      <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Estimate</p>
                      <p className="text-white font-bold text-sm">{formatBudget(p.budget)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Modified</p>
                      <p className="text-slate-400 text-xs font-medium">
                        {formatDate(p.updatedAt ?? p.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="action-row-icon-buttons">
                    {canEditProject && (
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="text-primary hover:bg-primary/10"
                        aria-label="Edit"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                    )}
                    {canViewEstimates && (
                      <button
                        type="button"
                        onClick={() => navigate(`/projects/${p.projectId}/estimates`)}
                        className="text-slate-300 hover:bg-white/5"
                        aria-label="Details"
                      >
                        <span className="material-symbols-outlined">visibility</span>
                      </button>
                    )}
                    {canDeleteProject && (
                      <button
                        type="button"
                        onClick={() => handleDelete(p.projectId)}
                        className="text-red-400 hover:bg-red-500/10"
                        aria-label="Delete"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </ListPageMobileStack>
        </ListPageContentSection>
      </ListPageRoot>

      <ProjectFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        project={editingId != null ? projects.find((p) => p.projectId === editingId) ?? null : null}
        onSuccess={loadProjects}
      />
      {deleteConfirmationDialog}
    </DarkLayout>
  );
};

export default Projects;
